import { useId, useState } from 'react';
import { ArrowDownRight, ArrowRight, ArrowUpRight, CircleAlert, Info, Minus } from 'lucide-react';
import { DATE_BASIS_LABEL } from 'modules/arena/reports/model/areas';
import { formatPeriod } from 'modules/arena/reports/model/dates';
import { describeComparison, formatMetric, formatValue } from 'modules/arena/reports/model/format';
import { Metric, ReportMeta } from 'modules/arena/reports/model/types';
import { cn } from 'shared/utils/cn';

interface KpiCardProps {
  metric: Metric;
  meta: ReportMeta;
  onOpen?: () => void;
}

const TONE_CLASS = {
  good: 'bg-success-soft text-success',
  bad: 'bg-danger-soft text-danger',
  neutral: 'bg-chip text-ink-secondary',
  none: 'bg-chip text-ink-secondary',
};

export function KpiCard({ metric, meta, onOpen }: KpiCardProps) {
  const helpId = useId();
  const [showHelp, setShowHelp] = useState(false);
  const comparison = describeComparison(metric.comparison, metric.polarity);
  const unavailable = metric.value === null;
  const ArrowIcon = comparison?.direction === 'up' ? ArrowUpRight : comparison?.direction === 'down' ? ArrowDownRight : Minus;
  const toneWord = comparison?.tone === 'good' ? 'favorável' : comparison?.tone === 'bad' ? 'desfavorável' : '';

  return (
    <article
      aria-labelledby={`${helpId}-label`}
      className={cn(
        'group relative flex h-full flex-col rounded-2xl border bg-surface p-4 shadow-card transition-shadow',
        metric.partial ? 'border-warning/50' : 'border-border/40',
        onOpen && 'hover:border-primary/40 hover:shadow-card-hover'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 id={`${helpId}-label`} className="text-xs font-semibold uppercase tracking-wide text-primary-dark">
          {metric.label}
        </h3>
        <button
          type="button"
          onClick={() => setShowHelp((current) => !current)}
          aria-expanded={showHelp}
          aria-controls={helpId}
          aria-label={`O que é "${metric.label}"?`}
          className="relative z-10 -m-1 rounded-md p-1 text-ink-secondary hover:bg-chip hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

      <p className={cn('mt-2 font-heading text-2xl font-bold', unavailable ? 'text-ink-secondary' : 'text-ink')}>
        {formatMetric(metric)}
      </p>
      {unavailable && metric.unavailableReason && <p className="text-xs text-ink-secondary">{metric.unavailableReason}</p>}
      {metric.detail && !unavailable && <p className="text-xs text-ink-secondary">{metric.detail}</p>}

      {comparison && (
        <p className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
          <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold', TONE_CLASS[comparison.tone])}>
            {comparison.direction && <ArrowIcon className="h-3 w-3" aria-hidden="true" />}
            {comparison.text}
            {toneWord && <span className="sr-only"> ({toneWord})</span>}
          </span>
          {meta.previousPeriod && comparison.tone !== 'none' && (
            <span className="text-ink-secondary">
              vs {formatPeriod(meta.previousPeriod.start, meta.previousPeriod.end)} (
              {formatValue(metric.comparison?.previousValue ?? null, metric.unit)})
            </span>
          )}
        </p>
      )}

      <p className="mt-auto pt-3 text-[11px] text-ink-secondary">
        {formatPeriod(meta.period.start, meta.period.end)}
        {metric.dateBasis && (metric.dateBasis === ('misto' as typeof metric.dateBasis) ? ' · datas próprias de cada item' : ` · por ${DATE_BASIS_LABEL[metric.dateBasis]}`)}
      </p>

      {metric.partial && (
        <p className="mt-1 flex items-center gap-1 text-[11px] font-medium text-warning">
          <CircleAlert className="h-3 w-3" aria-hidden="true" />
          Dado parcial — pode mudar na próxima atualização
        </p>
      )}

      {showHelp && (
        <p id={helpId} role="note" className="mt-2 rounded-lg bg-chip px-3 py-2 text-xs text-ink">
          {metric.help}
        </p>
      )}

      {onOpen && (
        <button
          type="button"
          onClick={onOpen}
          className="mt-3 flex items-center gap-1 self-start text-xs font-semibold text-primary-dark hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Ver detalhamento
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </button>
      )}
    </article>
  );
}

export function KpiGrid({ metrics, meta, onOpen }: { metrics: Metric[]; meta: ReportMeta; onOpen?: (metric: Metric) => void }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
      {metrics.map((metric) => (
        <KpiCard key={metric.id} metric={metric} meta={meta} onOpen={onOpen && metric.drillDown ? () => onOpen(metric) : undefined} />
      ))}
    </div>
  );
}
